import { hasSelectedPropertyTravelProducts, resolveSelectedOptionalBundles } from './propertyMultiRoomOptionalBundles'
import AccommodationIndividualSelectRoomOptionTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTile'
import AccommodationIndividualSelectRoomOptionTileCTA from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileCTA'
import AccommodationIndividualSelectRoomOptionTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileDetails'
import AccommodationIndividualSelectRoomOptionTilePricing from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTilePricing'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePropertyRatesByRoom } from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import PropertyRatePrice from './PropertyRatePrice'
import { getPropertyRatePassportOffer } from './singlePropertyUtils'

interface Props {
  otherRoomsHaveTravelProducts: boolean
  rate: App.EnrichedPropertyRate
  isSelected: boolean
  optionVariant: 'stacked' | 'carousel'
  property: App.Property.Property
  occupancyRoom: OfferPageRoom
  onSelect: (optionId: string) => void
}

function PropertyIndividualRoomSelectRoomOption(props: Props) {
  const {
    otherRoomsHaveTravelProducts,
    rate,
    isSelected,
    optionVariant,
    property,
    occupancyRoom,
    onSelect,
  } = props
  const intl = useIntl()
  const propertyTimezone = property.location.timezone
  const passportOffer = useMemo(() => getPropertyRatePassportOffer(rate, property), [property, rate])
  const showInclusions = (
    !!passportOffer ||
    rate.luxPlusInclusions.length > 0 ||
    rate.bonusInclusions.length > 0 ||
    rate.bundleInclusions.length > 0 ||
    rate.inclusions.length > 0
  )
  const cancellationPolicy = propertyTimezone ? (
    <PropertyCancellationPolicy
      rate={rate}
      propertyTimeZone={propertyTimezone}
      shouldClarifyRefundIsHotelOnly={otherRoomsHaveTravelProducts || rate.includesFlights || !!rate.bundle || resolveSelectedOptionalBundles(rate, occupancyRoom.optionalBundleIds).length > 0}
    />
  ) :
    undefined

  const onSelectRate = useCallback(() => {
    onSelect(rate.optionId)
  }, [onSelect, rate])

  return (
    <AccommodationIndividualSelectRoomOptionTile isSelected={isSelected}>
      <AccommodationIndividualSelectRoomOptionTileDetails
        variant={optionVariant}
        rateName={rate.name}
        tagLineLabel={<>
          {/* NB: Accommodation service synthesises a `promotion` for tactical rates. */}
          {!!rate.promotion && <PropertyPromotionLabel promotion={rate.promotion} />}
          {!rate.promotion && !!rate.hasAppliedDiscount && <ProductTypeLabel productType="limited_time_special" />}
        </>}
        cancellationPolicy={cancellationPolicy}
      >
        {rate.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
        {showInclusions && <PropertyRateInclusions
          property={property}
          inclusions={rate.inclusions}
          bonusInclusions={rate.bonusInclusions}
          luxPlusInclusions={rate.luxPlusInclusions}
          bundleInclusions={rate.bundleInclusions}
          passportOffer={passportOffer}
          maxHeight={208}
        />}
      </AccommodationIndividualSelectRoomOptionTileDetails>
      <AccommodationIndividualSelectRoomOptionTilePricing>
        <PropertyRatePrice rate={rate} occupancyRoom={occupancyRoom} />
      </AccommodationIndividualSelectRoomOptionTilePricing>
      <AccommodationIndividualSelectRoomOptionTileCTA>
        {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>
          {intl.formatMessage({
            id: '1IRx8lbZVkU7PYtgPvtT',
            defaultMessage: 'Selected',
            description: 'Button label indicating this room option is currently selected',
          })}
        </TextButton>}
        {!isSelected && <TextButton kind="secondary" onClick={onSelectRate}>
          {intl.formatMessage({
            id: 'p_umY21zQsl1hqz1rtlE',
            defaultMessage: 'Select',
            description: 'Button label to select this room option on the hotel or room booking page',
          })}
        </TextButton>}
      </AccommodationIndividualSelectRoomOptionTileCTA>
    </AccommodationIndividualSelectRoomOptionTile>
  )
}

export default withOfferPageAndReduxState((state, appState, props: Props) => ({
  otherRoomsHaveTravelProducts: hasSelectedPropertyTravelProducts(state.rooms, getActivePropertyRatesByRoom(state, appState), props.occupancyRoom.id),
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
}), PropertyIndividualRoomSelectRoomOption)
